import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './Broadcast.css';

const TEMPLATE_VARS = [
  { tag: '{{firstname}}', desc: "Recipient's first name" },
  { tag: '{{lastname}}',  desc: "Recipient's last name" },
  { tag: '{{fullname}}',  desc: 'Full name' },
  { tag: '{{email}}',     desc: 'Email address' },
];

export default function Broadcast() {
  const { showToast } = useToast();
  const [targetType, setTargetType] = useState('all');
  const [eventId,    setEventId]    = useState('');
  const [email,      setEmail]      = useState('');
  const [subject,    setSubject]    = useState('');
  const [message,    setMessage]    = useState('');
  const [events,     setEvents]     = useState([]);
  const [history,    setHistory]    = useState([]);
  const [sending,    setSending]    = useState(false);
  const [errors,     setErrors]     = useState({});

  useEffect(() => {
    api.get('/admin/broadcast/events').then(r => setEvents(r.data || [])).catch(() => {});
    loadHistory();
  }, []);

  const loadHistory = () =>
    api.get('/admin/broadcast/history').then(r => setHistory(r.data || [])).catch(() => {});

  const insertVar = (tag) => setMessage(m => m + tag);

  const validate = () => {
    const e = {};
    if (!subject.trim()) e.subject = 'Subject is required.';
    if (!message.trim()) e.message = 'Message is required.';
    if (targetType === 'event' && !eventId) e.event = 'Select an event.';
    if (targetType === 'individual' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) { showToast('Please fix the errors', 'warning'); return; }
    setSending(true);
    try {
      const payload = { subject, message, target_type: targetType };
      if (targetType === 'event')      payload.target_event_id = eventId;
      if (targetType === 'individual') payload.target_email = email;
      const r = await api.post('/admin/broadcast/send', payload);
      showToast(`Broadcast sent! ${r.data.sent || 0} delivered, ${r.data.failed || 0} failed.`, 'success');
      setSubject(''); setMessage(''); setEmail('');
      loadHistory();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send broadcast', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="broadcast-page">
        <div className="page-header">
          <div>
            <h1>Broadcast Email</h1>
            <p>Send emails to all registrants, event attendees, or individuals.</p>
          </div>
        </div>

        <div className="broadcast-layout">
          {/* Composer */}
          <div className="broadcast-composer">
            <div className="form-card">
              <h2 className="card-section-title">Recipients</h2>
              <div className="target-tabs">
                {[
                  { key:'all',        label:'All Registrants', icon:'👥' },
                  { key:'event',      label:'By Event',        icon:'📅' },
                  { key:'individual', label:'Individual',      icon:'👤' },
                ].map(t => (
                  <button
                    key={t.key}
                    className={`target-tab ${targetType === t.key ? 'active' : ''}`}
                    onClick={() => setTargetType(t.key)}
                  >
                    <span>{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>

              {targetType === 'event' && (
                <div className="form-group" style={{marginTop:'1rem'}}>
                  <label className="form-label">Select Event</label>
                  <select
                    className={`form-control ${errors.event ? 'is-invalid' : ''}`}
                    value={eventId}
                    onChange={e => setEventId(e.target.value)}
                  >
                    <option value="">— Choose an event —</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.title} ({e.registrant_count} registrants)</option>
                    ))}
                  </select>
                  {errors.event && <span className="invalid-feedback">{errors.event}</span>}
                </div>
              )}

              {targetType === 'individual' && (
                <div className="form-group" style={{marginTop:'1rem'}}>
                  <label className="form-label">Recipient Email</label>
                  <input
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                  {errors.email && <span className="invalid-feedback">{errors.email}</span>}
                </div>
              )}
            </div>

            <div className="form-card">
              <h2 className="card-section-title">Compose Message</h2>
              <div className="form-group">
                <label className="form-label">Subject <span className="req">*</span></label>
                <input
                  className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Email subject line"
                />
                {errors.subject && <span className="invalid-feedback">{errors.subject}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Message <span className="req">*</span></label>
                <textarea
                  className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                  rows={10}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Write your message here. Use template variables for personalization."
                />
                {errors.message && <span className="invalid-feedback">{errors.message}</span>}
              </div>
              <button className="btn btn-primary" onClick={handleSend} disabled={sending} style={{width:'100%'}}>
                {sending ? <><span className="spinner spinner-sm" /> Sending…</> : '📨 Send Broadcast'}
              </button>
            </div>
          </div>

          {/* Sidebar: template vars + history */}
          <div className="broadcast-sidebar">
            <div className="form-card">
              <h2 className="card-section-title">Template Variables</h2>
              <p className="form-hint" style={{marginBottom:'0.875rem'}}>Click to insert. They'll be replaced per recipient.</p>
              <div className="var-list">
                {TEMPLATE_VARS.map(v => (
                  <button key={v.tag} className="var-chip" onClick={() => insertVar(v.tag)}>
                    <code>{v.tag}</code>
                    <span>{v.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-card">
              <h2 className="card-section-title">Recent Broadcasts</h2>
              {history.length === 0 ? (
                <p className="form-hint">No broadcasts sent yet.</p>
              ) : (
                <div className="history-list">
                  {history.slice(0, 8).map(h => (
                    <div className="history-item" key={h.id}>
                      <div className="history-subject">{h.subject}</div>
                      <div className="history-meta">
                        <span className="badge badge-primary">{h.target_type}</span>
                        <span>{h.recipient_count || h.sent_count || 0} sent</span>
                        <span className="text-muted">
                          {new Date(h.sent_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
